import { z } from "zod";

/**
 * Sports venue protocol (ADR-0016): Townsy Sports Center. Free to play: no chips, no prizes; scores last for the
 * visit. The browser only sends intents; the server owns the balls, the AI goalie and the AI tennis player and decides
 * every outcome (shot makes, steals, kick spread, saves, rallies) with its own randomness.
 *
 * Where you can act is decided by where you stand (the server knows every pose): on the basketball court you can shoot,
 * pass or steal; on the soccer pitch you can kick; on a tennis court side you can join (Play / Play vs AI), swing and
 * leave.
 */
/** A charged action's power: 0 (a tap) .. 1 (full meter). Out-of-range values are clamped by the server. */
const Power = z.number().finite();
/** A world yaw (radians, 0 = north / +z); the server keeps it within reach of the visitor's facing. */
const Yaw = z.number().finite().min(-10).max(10);

export const SportsAction = z.discriminatedUnion("type", [
  /** Basketball: shoot at the nearest hoop with the released power (you must hold the ball). Charge and release. */
  z.object({ type: z.literal("shoot"), power: Power }).strict(),
  /** Basketball: pass to the nearest player in front of you (or a short toss forward). */
  z.object({ type: z.literal("pass") }).strict(),
  /** Basketball: try to take the ball from a player within 1.5 m. */
  z.object({ type: z.literal("steal") }).strict(),
  /** Soccer: kick the ball (within 1.3 m) with the released power toward `yaw` (default: the way you face). */
  z.object({ type: z.literal("kick"), power: Power.optional(), yaw: Yaw.optional() }).strict(),
  /** Soccer: a light touch (a short pass along the ground) toward `yaw`, for fine positioning. */
  z.object({ type: z.literal("tap"), yaw: Yaw.optional() }).strict(),
  /** Tennis: take the side of the court you stand on; `vsAi` puts the AI on the other side at once. */
  z.object({ type: z.literal("tennis_play"), vsAi: z.boolean().optional() }).strict(),
  /**
   * Tennis: serve (your serve) or return the ball (within reach, before its second bounce). `aim` picks the part of the
   * opponent's court across the net (world x: -1 west, 0 centre, 1 east); default: from the way you face.
   */
  z.object({ type: z.literal("swing"), aim: z.union([z.literal(-1), z.literal(0), z.literal(1)]).optional() }).strict(),
  /** Tennis: give up your side. */
  z.object({ type: z.literal("tennis_leave") }).strict(),
]);
export type SportsAction = z.infer<typeof SportsAction>;
export type SportsActionType = SportsAction["type"];

/**
 * Charged actions: holding the key or button fills the power meter from 0 to 1 in this time, then it stays full;
 * releasing sends the power.
 */
export const SPORTS_CHARGE_MS = 1100;

/** Basketball: the release power that drops the ball in from `distance` metres (the meter's sweet spot). */
export function basketballSweetSpot(distance: number): number {
  const d = Number.isFinite(distance) ? Math.max(0, distance) : 0;
  return Math.min(0.94, Math.max(0.3, 0.28 + d * 0.06));
}
/** Half-width of the "perfect" band around the sweet spot shown on the meter. */
export const BASKETBALL_SWEET_WINDOW = 0.06;

/** Soccer: how far a kick may be aimed away from the way you face, and one aim nudge (A / D or the arrow buttons). */
export const SOCCER_AIM_MAX_RAD = 0.6;
export const SOCCER_AIM_STEP_RAD = 0.035;
/** Soccer: kick range (m), for the client's hints; the server decides. */
export const SOCCER_KICK_RANGE = 1.3;

/** Tennis: a swing reaches a ball this close (horizontal metres) and this low, on your side, before its second bounce. */
export const TENNIS_REACH = 1.8;
export const TENNIS_REACH_HEIGHT = 2.6;
export type TennisAim = -1 | 0 | 1;
/** The part of the opponent's court a return aims at, from the way the player faces (world x: west, centre, east). */
export function tennisAimFromFacing(facing: number): TennisAim {
  const s = Math.sin(facing);
  return s < -0.35 ? -1 : s > 0.35 ? 1 : 0;
}

export type SportsZone = "basketball" | "soccer" | "tennis";

/** Tennis match rule shown to players: first to 7 points, win by 2; the serve alternates every point. */
export const TENNIS_POINTS_TO_WIN = 7;
export const TENNIS_WIN_BY = 2;

/** A ball on the wire: position (m) and velocity (m/s), rounded to centimetres: [x, y, z, vx, vy, vz]. */
export type SportsBallWire = [number, number, number, number, number, number];

export type BasketballPhase = "loose" | "held" | "shot" | "pass" | "scored";
export interface BasketballWire {
  ball: SportsBallWire;
  phase: BasketballPhase;
  /** Actor id of the player holding the ball (phase "held"). */
  holder: string | null;
  /** Name shown for the holder. */
  holderName: string | null;
  /** Best scorers this visit (at most 5): display name and points. */
  top: Array<{ name: string; points: number }>;
}

export type SoccerPhase = "live" | "goal" | "saved" | "out";
export interface SoccerWire {
  ball: SportsBallWire;
  phase: SoccerPhase;
  /** The AI goalie on its line: x (m), lean (-1 dive west .. 1 dive east) and whether it holds the ball. */
  goalie: { x: number; z: number; lean: number; holding: boolean };
  /** Totals this visit: goals scored against the AI goalie and shots it saved. */
  goals: number;
  saves: number;
  top: Array<{ name: string; goals: number; saved: number }>;
}

export type TennisPhase = "open" | "waiting" | "serve" | "rally" | "point" | "over";
export interface TennisSideWire {
  kind: "open" | "human" | "ai";
  actorId: string | null;
  name: string;
  points: number;
}
/** A side's swings: a counter (the client animates each new one), forehand or backhand, and whether it hit the ball. */
export interface TennisSwingWire {
  n: number;
  hand: "fore" | "back";
  hit: boolean;
}
export interface TennisCourtWire {
  phase: TennisPhase;
  ball: SportsBallWire | null;
  sides: [TennisSideWire, TennisSideWire];
  /** Who hit the ball in play last (null before the serve) and how often it has bounced since. */
  lastHitter: 0 | 1 | null;
  bounces: number;
  swings: [TennisSwingWire, TennisSwingWire];
  /** Side (0 south, 1 north) that serves this point. */
  server: 0 | 1;
  /** The AI player when it plays a side: [x, z, facing]. */
  ai: [number, number, number] | null;
  /** Short line for the scoreboard ("Ann to serve", "Point to AI", "Ann wins 7-5"). */
  message: string;
}

/** `venue.state` of the sports center. */
export interface SportsState {
  basketball: BasketballWire;
  soccer: SoccerWire;
  tennis: TennisCourtWire[];
}

/** `venue.private` of the sports center: where you stand and your own scores this visit. */
export interface SportsPrivate {
  zone: SportsZone | null;
  /** Tennis court (0/1) and side (0 south, 1 north) you stand on, when zone is "tennis". */
  court: number | null;
  side: number | null;
  /** The tennis side you play, if any. */
  playing: { court: number; side: number } | null;
  /** Basketball points, soccer goals, soccer shots the goalie saved, tennis matches won (this visit). */
  points: number;
  goals: number;
  saved: number;
  tennisWins: number;
}

/** Shape check for a received `venue.state` (the client never trusts a payload blindly). */
export function isSportsState(v: unknown): v is SportsState {
  if (!v || typeof v !== "object") return false;
  const s = v as Partial<SportsState>;
  return Boolean(s.basketball && Array.isArray(s.basketball.ball) && s.soccer && Array.isArray(s.soccer.ball) && Array.isArray(s.tennis));
}

export function isSportsPrivate(v: unknown): v is SportsPrivate {
  if (!v || typeof v !== "object") return false;
  const p = v as Partial<SportsPrivate>;
  return typeof p.points === "number" && typeof p.goals === "number" && "zone" in p;
}

/**
 * What happened, carried in a sports `venue.event`'s `data` so the overlay can show it big (founder 2026-10-14: "show
 * the outcome of the event, like basketball, "Missed Shot" or "Shot Made", and similar for Tennis and Soccer"). The
 * server decides every outcome; `by` is the actor whose shot, kick, point or match it is (null: the AI player).
 */
export const SPORTS_OUTCOMES = ["made", "missed", "goal", "saved", "wide", "over", "out", "point_won", "point_lost", "match_won"] as const;
export type SportsOutcomeKind = (typeof SPORTS_OUTCOMES)[number];
export interface SportsOutcome {
  sport: SportsZone;
  outcome: SportsOutcomeKind;
  by: string | null;
  /** Tennis: the other side's actor (null: the AI). A `match_won` event names the loser here. */
  against?: string | null;
  /** Basketball: points scored. */
  points?: number;
  /** Short plain reason ("Short: off the front of the rim", "Your shot landed out"). */
  detail?: string;
  /** Tennis: the score after the point or match, `by`'s side first. */
  score?: [number, number];
}

/** The outcome in a received event's data, or null (older servers, other events, malformed data). */
export function sportsOutcomeOf(data: unknown): SportsOutcome | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Partial<SportsOutcome>;
  if (d.sport !== "basketball" && d.sport !== "soccer" && d.sport !== "tennis") return null;
  if (typeof d.outcome !== "string" || !(SPORTS_OUTCOMES as readonly string[]).includes(d.outcome)) return null;
  if (d.by !== null && typeof d.by !== "string") return null;
  const score = Array.isArray(d.score) && d.score.length === 2 && d.score.every((n) => typeof n === "number") ? (d.score as [number, number]) : undefined;
  return {
    sport: d.sport,
    outcome: d.outcome,
    by: d.by ?? null,
    ...(typeof d.against === "string" || d.against === null ? { against: d.against } : {}),
    ...(typeof d.points === "number" ? { points: d.points } : {}),
    ...(typeof d.detail === "string" ? { detail: d.detail.slice(0, 120) } : {}),
    ...(score ? { score } : {}),
  };
}
